'use client'

import { useMemo } from 'react'
import {
  createTheme,
  ThemeProvider,
  StyledEngineProvider,
} from '@mui/material/styles'
import {
  ConnectionProvider,
  WalletProvider,
} from '@solana/wallet-adapter-react'
import { Connection, clusterApiUrl } from '@solana/web3.js'
import themeDark from '@theme/theme'
import { ExplorerContextProvider } from '@context/Explorer/context'
import {
  PhantomWalletAdapter,
  SolflareWalletAdapter,
} from '@solana/wallet-adapter-wallets'
import { AccountProvider } from '@helium/account-fetch-cache-hooks'
import { GovernanceProvider } from '@hooks/useGovernance'
import LanguageProvider from '../../localization/LanguageProvider'

import { ConnectButtonProvider } from '@dewicats/connect-button'
import { TipLinkWalletAdapter } from '@tiplink/wallet-adapter'

const rpcHost = process.env.NEXT_PUBLIC_REACT_APP_SOLANA_RPC_HOST
const magicKey = process.env.NEXT_PUBLIC_MAGIC_PUBLISHABLE_KEY

const MainProvider = ({ children }: { children: React.ReactNode }) => {
  const rpcHost =
    process.env.NEXT_PUBLIC_REACT_APP_SOLANA_RPC_HOST ?? clusterApiUrl('devnet')

  const connection = useMemo(
    () => new Connection(rpcHost, { commitment: 'confirmed' }),
    [rpcHost],
  )

  const baseTheme = themeDark

  const theme = createTheme({
    ...baseTheme,
  })

  const wallets = useMemo(
    () => [
      /**
       * Use TipLinkWalletAdapter here
       * Include the name of the dApp in the constructor
       * Pass the client id that the TipLink team provides
       * Choose from "dark", "light", "system" for the theme
       */
      new TipLinkWalletAdapter({
        title: 'Name of Dapp',
        clientId: '9d4a9ae4-e96c-451b-93a6-25d7d90bfd5f',
        theme: 'dark', // pick between "dark"/"light"/"system"
      }),
      ,
      new PhantomWalletAdapter(),
      new SolflareWalletAdapter(),
    ],
    [],
  )

  return (
    <StyledEngineProvider injectFirst>
      <LanguageProvider>
        <ThemeProvider theme={theme}>
          <AccountProvider connection={connection} commitment="confirmed">
            <ConnectionProvider endpoint={connection.rpcEndpoint}>
              <WalletProvider wallets={wallets} autoConnect>
                <ConnectButtonProvider
                  solanaRpcHost={rpcHost}
                  wallets={wallets}
                >
                  <GovernanceProvider>
                    <ExplorerContextProvider>
                      {children}
                    </ExplorerContextProvider>
                  </GovernanceProvider>
                </ConnectButtonProvider>
              </WalletProvider>
            </ConnectionProvider>
          </AccountProvider>
        </ThemeProvider>
      </LanguageProvider>
    </StyledEngineProvider>
  )
}

export default MainProvider
